import { createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { GuideTemplate } from "~/components/cih/guide-template";
import { getPublishedGuideFn, type GuideRow } from "~/lib/functions/cih/guides";
import { getPublishedPlaybookFn } from "~/lib/functions/cih/playbooks";
import { getPublishedSeriesMembersFn } from "~/lib/functions/cih/series";
import type { PlaybookRow } from "~/lib/server/cih/playbooks";
import {
  annotateBodyHeadings,
  type BodyHeading,
} from "~/lib/shared/cih/body-toc";
import { buildGuideJsonLd } from "~/lib/shared/cih/guide-jsonld";
import {
  buildSeriesNav,
  type SeriesNavResult,
} from "~/lib/shared/cih/library-series";
import type { JsonValue } from "~/lib/shared/json";
import { createBrandMeta } from "~/lib/shared/meta";

type GuideSeriesNav = SeriesNavResult<
  GuideRow & { slug: string },
  PlaybookRow & { slug: string }
>;

type LoaderData = {
  title: string;
  guide: GuideRow;
  /** Tiptap doc with stable `id` attrs on every heading. */
  bodyDoc: JsonValue | null;
  /** Ordered H2 anchors. */
  bodyHeadings: BodyHeading[];
  /** Previous/next series nav, or null when the guide belongs to no series. */
  seriesNav: GuideSeriesNav | null;
};

export const Route = createFileRoute("/_public/coaching-business/$guideSlug")({
  staticData: { title: "Guide" },
  loader: async ({ params }): Promise<LoaderData> => {
    const guide = await getPublishedGuideFn({
      data: { slug: params.guideSlug },
    });
    // Playbooks live at /coaching-business/playbooks/{slug}, but historic
    // internal links point playbook slugs at this flat guide path, which 404s.
    // Resolve playbook-ness only once the guide lookup has missed: a published
    // guide never reaches this branch, so the 301 — permanent and hard-cached
    // by browsers and Google — structurally cannot capture a live guide, and it
    // stops firing on its own if the playbook is later unpublished.
    if (!guide) {
      const playbook = await getPublishedPlaybookFn({
        data: { slug: params.guideSlug },
      });
      if (playbook) {
        throw redirect({
          to: "/coaching-business/playbooks/$slug",
          params: { slug: params.guideSlug },
          statusCode: 301,
        });
      }
      throw notFound();
    }
    const { doc: bodyDoc, headings: bodyHeadings } = annotateBodyHeadings(
      guide.body,
    );

    // Skip the sibling fetch entirely when the guide has no series — the
    // common case, so it should cost nothing.
    let seriesNav: LoaderData["seriesNav"] = null;
    if (guide.seriesFamily) {
      const siblings = await getPublishedSeriesMembersFn({
        data: { family: guide.seriesFamily },
      });
      seriesNav = buildSeriesNav(
        guide.seriesFamily,
        siblings.guides,
        siblings.playbooks,
        { type: "guide", slug: params.guideSlug },
      );
    }

    return { title: guide.title, guide, bodyDoc, bodyHeadings, seriesNav };
  },
  head: ({
    loaderData,
    params,
  }: {
    loaderData?: LoaderData;
    params: { guideSlug: string };
  }) => {
    const guide = loaderData?.guide;
    const url = `https://dreamcoachmatch.com/coaching-business/${params.guideSlug}`;
    const base = createBrandMeta({
      title: guide
        ? `${guide.metaTitle ?? guide.title} | Coaching Business`
        : "Coaching Business Guide",
      description:
        guide?.description ||
        "A tactical guide for coaches building their practice.",
      ogUrl: url,
      canonical: url,
    });
    if (!guide) return base;

    const graph = buildGuideJsonLd({
      slug: params.guideSlug,
      title: guide.title,
      description: guide.description,
      faqs: guide.faqs ?? [],
      heroImageUrl: guide.heroImageUrl,
      publishedAt: guide.publishedAt,
      updatedAt: guide.updatedAt,
    });

    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(graph) },
      ],
    };
  },
  component: GuideDetailPage,
});

function GuideDetailPage() {
  const { guide, bodyDoc, bodyHeadings, seriesNav } = Route.useLoaderData();
  return (
    <GuideTemplate
      guide={guide}
      bodyDoc={bodyDoc}
      headings={bodyHeadings}
      seriesNav={seriesNav}
    />
  );
}
